import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { adriva_getLatestDQJobDetails, adriva_downloadDQReport, adriva_downloadSavedDQReport, adriva_getDQEngine } from "../API";
import DQMonitorDashboard from "./DQMonitorDashboard";
import DQDashboard from "./DQDashboard";

const T = {
  bg: "#eef1f6", bgCard: "#ffffff",
  textPrimary: "#0f1923", textSecond: "#1e2d3d", textMuted: "#445566",
  brand: "#1e3a6e", brandLight: "#dde7f5", brandMid: "#90aed0", border: "#c8d4e0",
  red: "#991b1b", redBg: "#fef2f2", redBorder: "#fca5a5",
};

interface SelectedJob {
  job_id: string;
  stats: any | null;
  rules: any[] | null;
}

type Props = { appNm: string; dmData: any[] };

const DQDashboardContainer: React.FC<Props> = ({ appNm, dmData }) => {
  const initialDMId = dmData?.length ? dmData[0].id : "";
  const [selectedDMEntityId, setSelectedDMEntityId] = useState<string>(initialDMId);
  const [selectedJob, setSelectedJob] = useState<SelectedJob | null>(null);
  const [loading, setLoading]         = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobError, setJobError]       = useState<string | null>(null);

  const selectedEntity = useMemo(() => dmData?.find(t => t.id === selectedDMEntityId), [dmData, selectedDMEntityId]);
  // applications on the v2 engine (reviewed rules) get the MONITORING dashboard; legacy ones keep this one
  const [engine, setEngine] = useState<string>("");
  useEffect(() => {
    if (!selectedEntity?.entityId) { setEngine(""); return; }
    adriva_getDQEngine(selectedEntity.entityId).then((e: string) => setEngine(e)).catch(() => setEngine("legacy"));
  }, [selectedEntity?.entityId]);

  const handleDownload = async () => {
    if (!selectedEntity) return;
    const fileName =
      `DQ_Report_${selectedEntity.label}_${new Date().toLocaleDateString()}.xlsx`;
    try {
      setDownloading(true);
      const blob = await adriva_downloadDQReport(
        selectedEntity.entityId,
        selectedEntity.id,
      );
      const url = window.URL.createObjectURL(blob);          // blob is a Blob now
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      // POST stream may have timed out, but the server saved a copy — try it.
      const savedName = error?.response?.headers?.["x-dq-report-file"];
      if (savedName) {
        try {
          await adriva_downloadSavedDQReport(savedName, fileName);
          return;
        } catch {
          /* fall through to the alert */
        }
      }
      console.error("Download failed:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const fetchLatestDQDashboard = useCallback(() => {
    if (!selectedEntity) return;
    setLoading(true); setJobError(null); setSelectedJob(null);
    try {
      adriva_getLatestDQJobDetails(
        selectedEntity.entityId, selectedEntity.id,
        (res: any) => { setSelectedJob(res.data); setLoading(false); },
        (e: any)   => { setJobError(`Failed to load: ${e.message || "Unknown error"}`); setLoading(false); }
      );
    } catch (e: any) {
      setJobError("External API call failed to initialize.");
      setLoading(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (dmData?.length && selectedDMEntityId) {
      fetchLatestDQDashboard();
    } else if (dmData?.length && !selectedDMEntityId) {
      setSelectedDMEntityId(dmData[0].id);
    }
  }, [selectedDMEntityId, fetchLatestDQDashboard, dmData]);

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", background: T.bg, minHeight: "100%" }}>
      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
        background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10,
        padding: "12px 18px", marginBottom: 18,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        {/* Entity tabs */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {dmData?.map((t: any) => {
            const active = selectedDMEntityId === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedDMEntityId(t.id)}
                style={{
                  padding: "6px 16px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, transition: "all 0.15s",
                  background: active ? T.brand : T.brandLight,
                  color: active ? "#fff" : T.brand,
                  boxShadow: active ? "0 2px 6px rgba(30,58,110,0.3)" : "none",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Export button */}
        {engine !== "v2" && <button
          onClick={handleDownload}
          disabled={downloading || !selectedJob}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "7px 16px", borderRadius: 7, cursor: downloading || !selectedJob ? "not-allowed" : "pointer",
            border: `1px solid ${T.brand}`, background: "transparent",
            color: T.brand, fontSize: 12, fontWeight: 600,
            opacity: !selectedJob ? 0.5 : 1,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (selectedJob && !downloading) { (e.currentTarget as HTMLButtonElement).style.background = T.brandLight; } }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
        >
          {downloading ? (
            <>
              <Spinner size="sm" style={{ width: 12, height: 12 }} />
              Generating…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Export Excel
            </>
          )}
        </button>}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "60px 0", background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}` }}>
          <Spinner animation="border" style={{ width: 20, height: 20, borderWidth: 2, color: T.brand }} />
          <span style={{ fontSize: 13, color: T.textMuted, fontWeight: 500 }}>Loading DQ results…</span>
        </div>
      )}

      {jobError && (
        <div style={{ background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.red, marginBottom: 4 }}>Error Loading Data</div>
          <div style={{ fontSize: 12, color: T.red }}>{jobError}</div>
        </div>
      )}

      {engine === "v2" && selectedEntity && <DQMonitorDashboard entityId={selectedEntity.entityId} />}
      {engine !== "v2" && !loading && !jobError && selectedJob?.stats && selectedJob.rules && (
        <DQDashboard dq={selectedJob.stats} rules={selectedJob.rules} />
      )}

      {!loading && !jobError && selectedJob && !selectedJob.stats && (
        <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "32px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.textSecond }}>No Results Found</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
            No recent DQ execution results found for <strong>{selectedDMEntityId}</strong>.
          </div>
        </div>
      )}
    </div>
  );
};

export default DQDashboardContainer;